import { validateVehicleBlueprint, type VehicleBlueprint, type VehicleStyle } from "@neon-arena/shared";
import { VehicleShowcase } from "./VehicleShowcase.js";
import type { AuthClient } from "../auth/AuthClient.js";

const CUSTOM_VEHICLE_CAP = 5;
const HEX_RE = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

function wsToHttp(url: string): string {
  return url.replace(/^wss?:\/\//, (m) => (m === "wss://" ? "https://" : "http://"));
}

function stripFences(text: string): string {
  return text.replace(/^```(?:json)?\s*/i, "").replace(/\s*```\s*$/, "");
}

/** Validate that a parsed object is a valid VehicleStyle block. */
function validateStyleBlock(raw: unknown): { ok: true; style: VehicleStyle } | { ok: false; errors: string[] } {
  if (!raw || typeof raw !== "object") return { ok: false, errors: ["expected a JSON object"] };
  const o = raw as Record<string, unknown>;
  const errors: string[] = [];
  for (const key of ["primaryColor", "secondaryColor", "trimColor"] as const) {
    if (typeof o[key] !== "string" || !HEX_RE.test(o[key] as string))
      errors.push(`${key}: must be a hex colour like #RRGGBB`);
  }
  if (typeof o["wireframe"] !== "boolean") errors.push("wireframe: must be true or false");
  if (typeof o["glow"] !== "boolean")      errors.push("glow: must be true or false");
  const extra = Object.keys(o).filter((k) => !["primaryColor","secondaryColor","trimColor","wireframe","glow"].includes(k));
  if (extra.length) errors.push(`unexpected fields: ${extra.join(", ")}`);
  if (errors.length) return { ok: false, errors };
  return {
    ok: true,
    style: {
      primaryColor:   o["primaryColor"]   as string,
      secondaryColor: o["secondaryColor"] as string,
      trimColor:      o["trimColor"]      as string,
      wireframe:      o["wireframe"]      as boolean,
      glow:           o["glow"]           as boolean,
    },
  };
}

/** Build the complete style-edit prompt including the user's description. */
function buildStylePrompt(bp: VehicleBlueprint, description: string): string {
  const { style } = bp;
  const desc = description.trim() || "make it look cool";
  return `You are a vehicle colour designer for a combat racing game called Neon Arena. Vehicles are rendered as low-poly 3D cars with neon lighting effects.

I have a vehicle called "${bp.name}" and I want to change its colour scheme based on this request:

REQUEST: ${desc}

Current colours (for reference):
  Primary (main body)     : ${style.primaryColor}
  Secondary (panels)      : ${style.secondaryColor}
  Trim (accents/glow)     : ${style.trimColor}
  Wireframe overlay active: ${style.wireframe}
  Neon glow active        : ${style.glow}

Translate my request into a colour scheme. Output ONLY valid JSON in this exact format — no explanation, no markdown, no image, no code fences:

{"primaryColor":"#RRGGBB","secondaryColor":"#RRGGBB","trimColor":"#RRGGBB","wireframe":true,"glow":true}

Rules:
- All colours must be 6-digit hex codes e.g. #ff6600
- Translate themes/animals/concepts into appropriate hex colours (tiger = orange #c85a00 body, black #1a1a1a panels, orange trim; ice = pale blue #a8d8ea body, white #e8f4f8 panels, cyan #22d3ee trim, etc.)
- primaryColor is the dominant body colour — keep it bold and readable
- secondaryColor is panels and detail areas
- trimColor is accent lines and the glow colour
- wireframe true/false adds a glowing polygon edge mesh over the body
- glow true/false enables bloom on headlights and trim
- Output JSON only — absolutely no other text before or after`;
}

type ModalMode = "create" | "edit-style";

export class AIBuilderModal {
  private root: HTMLElement;
  private titleEl: HTMLElement;
  private stepLabel1: HTMLElement;
  private stepLabel2: HTMLElement;
  private stepLabel3: HTMLElement;
  private styleDescRow: HTMLElement;
  private styleDescInput: HTMLInputElement;
  private promptTextEl: HTMLTextAreaElement;
  private jsonInputEl: HTMLTextAreaElement;
  private validationEl: HTMLElement;
  private previewWrap: HTMLElement;
  private previewCanvas: HTMLCanvasElement;
  private nameInput: HTMLInputElement;
  private nameRow: HTMLElement;
  private saveBtn: HTMLButtonElement;
  private saveErrorEl: HTMLElement;
  private slotCountEl: HTMLElement;
  private showcase: VehicleShowcase | null = null;
  private validBlueprint: VehicleBlueprint | null = null;
  private promptCache: string | null = null;
  private mode: ModalMode = "create";
  private editingBlueprint: VehicleBlueprint | null = null;

  constructor(
    private auth: AuthClient | null,
    private getServerUrl: () => string,
    private onSaved: () => void,
  ) {
    this.root          = document.getElementById("ai-builder-modal")!;
    this.titleEl       = document.getElementById("ai-modal-title")!;
    this.stepLabel1    = document.getElementById("ai-step-label-1")!;
    this.stepLabel2    = document.getElementById("ai-step-label-2")!;
    this.stepLabel3    = document.getElementById("ai-step-label-3")!;
    this.styleDescRow  = document.getElementById("ai-style-desc-row")!;
    this.styleDescInput = document.getElementById("ai-style-desc") as HTMLInputElement;
    this.promptTextEl  = document.getElementById("ai-prompt-text") as HTMLTextAreaElement;
    this.jsonInputEl   = document.getElementById("ai-json-input") as HTMLTextAreaElement;
    this.validationEl  = document.getElementById("ai-validation-msg")!;
    this.previewWrap   = document.getElementById("ai-preview-wrap")!;
    this.previewCanvas = document.getElementById("ai-preview-canvas") as HTMLCanvasElement;
    this.nameInput     = document.getElementById("ai-vehicle-name") as HTMLInputElement;
    this.nameRow       = document.getElementById("ai-name-row")!;
    this.saveBtn       = document.getElementById("ai-save-btn") as HTMLButtonElement;
    this.saveErrorEl   = document.getElementById("ai-save-error")!;
    this.slotCountEl   = document.getElementById("ai-slot-count")!;

    document.getElementById("ai-modal-close")?.addEventListener("click", () => this.close());
    document.getElementById("ai-copy-btn")?.addEventListener("click", () => void this.copyPrompt());
    this.jsonInputEl.addEventListener("input", () => this.handleJsonInput());
    this.saveBtn.addEventListener("click", () => void this.save());
    this.root.addEventListener("click", (e) => { if (e.target === this.root) this.close(); });
    this.styleDescInput.addEventListener("input", () => {
      if (this.mode === "edit-style" && this.editingBlueprint) {
        this.promptTextEl.value = buildStylePrompt(this.editingBlueprint, this.styleDescInput.value);
      }
    });
  }

  /** Open in "create new vehicle" mode. */
  open(): void {
    if (!this.auth?.isLoggedIn) return;
    this.mode = "create";
    this.editingBlueprint = null;
    this.applyModeUI();
    this.reset();
    this.root.classList.remove("hidden");
    void this.loadPrompt();
    void this.loadSlotCount();
  }

  /** Open in "edit style" mode for an existing custom vehicle. */
  openEdit(blueprint: VehicleBlueprint): void {
    if (!this.auth?.isLoggedIn) return;
    this.mode = "edit-style";
    this.editingBlueprint = blueprint;
    this.applyModeUI();
    this.reset();
    this.root.classList.remove("hidden");
    this.styleDescRow.classList.remove("hidden");
    this.styleDescInput.value = "";
    this.promptTextEl.value = buildStylePrompt(blueprint, "");
    // Pre-fill the name
    this.nameInput.value = blueprint.name;
    this.nameRow.classList.remove("hidden");
    this.slotCountEl.textContent = "";
  }

  close(): void {
    this.root.classList.add("hidden");
    this.destroyShowcase();
  }

  private applyModeUI(): void {
    if (this.mode === "create") {
      this.titleEl.textContent = "BUILD WITH AI";
      this.stepLabel1.textContent = "1 — COPY THIS PROMPT TO YOUR AI ASSISTANT";
      this.stepLabel2.textContent = "2 — PASTE THE FULL JSON BLUEPRINT HERE";
      this.stepLabel3.textContent = "3 — NAME AND SAVE";
      this.saveBtn.textContent = "SAVE VEHICLE";
    } else {
      this.titleEl.textContent = "EDIT STYLE WITH AI";
      this.stepLabel1.textContent = "1 — COPY THIS PROMPT — DESCRIBE THE LOOK YOU WANT";
      this.stepLabel2.textContent = "2 — PASTE THE STYLE JSON BACK HERE";
      this.stepLabel3.textContent = "3 — CONFIRM AND SAVE";
      this.saveBtn.textContent = "UPDATE STYLE";
    }
  }

  private reset(): void {
    this.jsonInputEl.value = "";
    this.setValidation("", "");
    this.previewWrap.classList.add("hidden");
    this.nameRow.classList.add("hidden");
    this.styleDescRow.classList.add("hidden");
    this.saveErrorEl.classList.add("hidden");
    this.saveBtn.disabled = false;
    this.validBlueprint = null;
    this.destroyShowcase();
  }

  private destroyShowcase(): void {
    if (this.showcase) { this.showcase.destroy(); this.showcase = null; }
  }

  private async loadPrompt(): Promise<void> {
    if (this.promptCache) { this.promptTextEl.value = this.promptCache; return; }
    this.promptTextEl.value = "Loading prompt…";
    try {
      const res = await fetch(`${wsToHttp(this.getServerUrl())}/api/vehicles/ai-prompt`);
      if (!res.ok) throw new Error(`${res.status}`);
      this.promptCache = await res.text();
      this.promptTextEl.value = this.promptCache;
    } catch {
      this.promptTextEl.value = "Failed to load — check server connection.";
    }
  }

  private async loadSlotCount(): Promise<void> {
    const token = this.auth?.token;
    if (!token) return;
    try {
      const res = await fetch(`${wsToHttp(this.getServerUrl())}/api/vehicles/custom`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json() as { vehicles: unknown[] };
      const remaining = CUSTOM_VEHICLE_CAP - data.vehicles.length;
      this.slotCountEl.textContent = `${remaining} of ${CUSTOM_VEHICLE_CAP} slots remaining`;
      this.slotCountEl.className = `ai-slot-count${remaining === 0 ? " ai-slot-full" : ""}`;
    } catch { /* ignore */ }
  }

  private async copyPrompt(): Promise<void> {
    const text = this.promptTextEl.value;
    if (!text || text.startsWith("Loading") || text.startsWith("Failed")) return;
    await navigator.clipboard.writeText(text);
    const btn = document.getElementById("ai-copy-btn")!;
    const orig = btn.textContent;
    btn.textContent = "COPIED ✓";
    setTimeout(() => { btn.textContent = orig; }, 1500);
  }

  private handleJsonInput(): void {
    const raw = this.jsonInputEl.value.trim();
    if (!raw) {
      this.setValidation("", "");
      this.previewWrap.classList.add("hidden");
      if (this.mode === "create") this.nameRow.classList.add("hidden");
      this.destroyShowcase();
      this.validBlueprint = null;
      return;
    }

    const text = stripFences(raw);
    let parsed: unknown;
    try { parsed = JSON.parse(text); }
    catch {
      this.setValidation("Invalid JSON — check for missing brackets or commas.", "error");
      this.previewWrap.classList.add("hidden");
      if (this.mode === "create") this.nameRow.classList.add("hidden");
      this.destroyShowcase();
      this.validBlueprint = null;
      return;
    }

    if (this.mode === "edit-style") {
      this.handleStyleInput(parsed);
    } else {
      this.handleBlueprintInput(parsed);
    }
  }

  private handleBlueprintInput(parsed: unknown): void {
    const result = validateVehicleBlueprint(parsed);
    if (!result.ok) {
      this.setValidation(result.errors.slice(0, 3).join("  ·  "), "error");
      this.previewWrap.classList.add("hidden");
      this.nameRow.classList.add("hidden");
      this.destroyShowcase();
      this.validBlueprint = null;
      return;
    }
    this.validBlueprint = result.blueprint;
    this.setValidation(`✓ Valid — budget ${result.budget.total.toFixed(1)} / ${result.budget.budget}`, "ok");
    this.previewWrap.classList.remove("hidden");
    this.nameRow.classList.remove("hidden");
    if (!this.nameInput.value) this.nameInput.value = result.blueprint.name;
    this.updatePreview(result.blueprint);
  }

  private handleStyleInput(parsed: unknown): void {
    const result = validateStyleBlock(parsed);
    if (!result.ok) {
      this.setValidation(result.errors.slice(0, 3).join("  ·  "), "error");
      this.previewWrap.classList.add("hidden");
      this.destroyShowcase();
      this.validBlueprint = null;
      return;
    }
    // Merge new style into the existing blueprint
    const merged: VehicleBlueprint = { ...this.editingBlueprint!, style: result.style };
    this.validBlueprint = merged;
    this.setValidation("✓ Valid style — preview updated", "ok");
    this.previewWrap.classList.remove("hidden");
    this.updatePreview(merged);
  }

  private setValidation(msg: string, type: "" | "ok" | "error"): void {
    this.validationEl.textContent = msg;
    this.validationEl.className = `ai-validation-msg${type ? ` ai-validation-${type}` : ""}`;
  }

  private updatePreview(blueprint: VehicleBlueprint): void {
    this.destroyShowcase();
    this.showcase = new VehicleShowcase(this.previewCanvas, [blueprint], () => {});
    this.showcase.start();
  }

  private async save(): Promise<void> {
    if (!this.validBlueprint || !this.auth?.isLoggedIn) return;
    const name = this.nameInput.value.trim();
    if (!name) {
      this.saveErrorEl.textContent = "Name your vehicle first.";
      this.saveErrorEl.classList.remove("hidden");
      return;
    }
    this.saveBtn.disabled = true;
    this.saveBtn.textContent = "SAVING…";
    this.saveErrorEl.classList.add("hidden");

    const isEdit = this.mode === "edit-style" && this.editingBlueprint;
    const url = isEdit
      ? `${wsToHttp(this.getServerUrl())}/api/vehicles/custom/${encodeURIComponent(this.editingBlueprint!.id)}`
      : `${wsToHttp(this.getServerUrl())}/api/vehicles/custom`;
    const method = isEdit ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${this.auth.token}` },
        body: JSON.stringify({ blueprint: this.validBlueprint, name }),
      });
      const data = await res.json() as Record<string, unknown>;
      if (!res.ok) {
        const details = Array.isArray(data["details"])
          ? `\n${(data["details"] as string[]).slice(0, 2).join("\n")}` : "";
        this.saveErrorEl.textContent = String(data["error"] ?? "save failed") + details;
        this.saveErrorEl.classList.remove("hidden");
        this.saveBtn.disabled = false;
        this.saveBtn.textContent = isEdit ? "UPDATE STYLE" : "SAVE VEHICLE";
        return;
      }
      this.onSaved();
      this.close();
    } catch {
      this.saveErrorEl.textContent = "Network error — check connection.";
      this.saveErrorEl.classList.remove("hidden");
      this.saveBtn.disabled = false;
      this.saveBtn.textContent = isEdit ? "UPDATE STYLE" : "SAVE VEHICLE";
    }
  }
}
